import { createFileRoute, useRouter } from "@tanstack/react-router";
import { HomeNav } from "@/components/home-nav";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";

import { unlockGuestSession } from "@/lib/guest.functions";

export const Route = createFileRoute("/guest-session/login")({
  head: () => ({
    meta: [
      { title: "Guest Session — Luiza Jesus do Prado" },
      { name: "description", content: "Private screening room with full-length performance films by Luiza Jesus do Prado." },
      { property: "og:title", content: "Guest Session — Luiza Jesus do Prado" },
      { property: "og:description", content: "Private screening room for invited guests." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: GuestLogin,
});

function GuestLogin() {
  const router = useRouter();
  const unlock = useServerFn(unlockGuestSession);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(false);
    const password = String(new FormData(e.currentTarget).get("password") ?? "");
    try {
      const { ok } = await unlock({ data: { password } });
      if (ok) await router.navigate({ to: "/guest-session" });
      else setError(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <HomeNav />
    <main id="main-content" className="flex min-h-svh flex-col items-center justify-center bg-ink px-6 py-24 text-bone">
      <div className="w-full max-w-md">
        <h1 className="mt-10 font-display text-[clamp(3rem,9vw,5.5rem)] font-light leading-[0.9] tracking-tight">
          Guest Session
        </h1>
        <p className="mt-6 text-sm leading-relaxed text-bone/80">
          This private area holds the performance videos, video art and other materials in full.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-bone/80">
          If you need an access code, request it by email:{" "}
          <a href="mailto:luizajesusdoprado@gmail.com?subject=Guest%20Session%20access" className="underline underline-offset-4 hover:text-bone">
            luizajesusdoprado@gmail.com
          </a>
          .
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-6">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/70">Password</span>
            <input
              name="password"
              type="password"
              required
              autoComplete="current-password"
              className="mt-3 w-full border-b border-bone/30 bg-transparent py-3 text-lg outline-none focus:border-bone"
            />
          </label>
          {error ? <p className="text-sm text-red">Incorrect password.</p> : null}
          <button
            type="submit"
            disabled={busy}
            className="border border-bone/60 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors hover:bg-bone hover:text-ink disabled:opacity-50"
          >
            {busy ? "Entering…" : "Enter"}
          </button>
        </form>

        <div className="mt-16 border-t border-bone/15 pt-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/65">Filmography</p>
          <div className="mt-4 flex gap-8 font-display text-2xl font-light">
            <a href="https://mubi.com/pt/cast/luiza-jesus-do-prado" target="_blank" rel="noopener noreferrer" className="hover:text-bone/60">
              MUBI ↗
            </a>
            <a href="https://www.imdb.com/name/nm19005373/" target="_blank" rel="noopener noreferrer" className="hover:text-bone/60">
              IMDb ↗
            </a>
          </div>
        </div>
      </div>
    </main>
    </>
  );
}
